// TailwindUI Elements for interactive components (dialog, dropdown, etc.)
import "@tailwindplus/elements";

// Vue components
import { createApp, defineAsyncComponent } from "vue";

// Twig component utils
import { initCheckboxTables } from "./utils/checkbox-table";
import { initAllTabs } from "./utils/tabs";

if (import.meta.hot) {
	import.meta.hot.accept(() => {
		console.log("HMR update applied");
	});
}

const componentMap = {
	"quick-search": defineAsyncComponent(
		() => import("./components/QuickSearch.vue"),
	),
	"data-table": defineAsyncComponent(
		() => import("./components/DataTable.vue"),
	),
	"projects-table": defineAsyncComponent(
		() => import("./components/ProjectsTable.vue"),
	),
	"updates-table": defineAsyncComponent(
		() => import("./components/UpdatesTable.vue"),
	),
	"team-table": defineAsyncComponent(
		() => import("./components/TeamTable.vue"),
	),
	"team-member-detail": defineAsyncComponent(
		() => import("./components/TeamMemberDetail.vue"),
	),
	"plugins-table": defineAsyncComponent(
		() => import("./components/PluginsTable.vue"),
	),
	"plugin-detail": defineAsyncComponent(
		() => import("./components/PluginDetail.vue"),
	),
	"tab-switcher": defineAsyncComponent(
		() => import("./components/TabSwitcher.vue"),
	),
	"lighthouse-scores": defineAsyncComponent(
		() => import("./components/LighthouseScores.vue"),
	),
} as const;

type ComponentKey = keyof typeof componentMap;

// Parses a single HTML attribute into a [key, value] prop pair.
// Attributes prefixed with ":" are treated as JSON-bound (like Vue's v-bind).
function parseAttr(attr: Attr): [string, unknown] {
	const bound = attr.name.startsWith(":");
	const name = (bound ? attr.name.slice(1) : attr.name).replace(
		/-([a-z])/g,
		(_, c: string) => c.toUpperCase(),
	);

	if (!bound) return [name, attr.value];

	try {
		return [name, JSON.parse(attr.value)];
	} catch {
		return [name, attr.value];
	}
}

function mountVueComponents(): void {
	for (const wrapper of document.querySelectorAll(".vue-wrapper")) {
		const child = wrapper.firstElementChild;
		if (!child) continue;

		const component = componentMap[child.tagName.toLowerCase() as ComponentKey];
		if (!component) continue;

		const props = Object.fromEntries(Array.from(child.attributes, parseAttr));
		wrapper.innerHTML = "";
		createApp(component, props).mount(wrapper);
	}
}

document.addEventListener("DOMContentLoaded", () => {
	mountVueComponents();
	initCheckboxTables();
	initAllTabs();
});
